import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Whether the story viewer should lay itself out for a device held in the
 * hand (map pinned to the top, story beneath) rather than a desktop screen
 * (story card floating over a full-viewport map).
 *
 * Viewport width alone does not answer this. Chrome on Android has a
 * per-site "Desktop site" toggle that pins the layout viewport to 980 CSS
 * px and ignores the page's `width=device-width`, and Mitigate is a
 * desktop-shaped app so readers do turn it on — the setting then follows
 * them to every `/s/` story link on the domain. Reported 17 Sep 2026: a
 * phone showed the floating-card desktop layout, because a `down("sm")`
 * query cannot match at a spoofed 980 px.
 *
 * `navigator.maxTouchPoints` is not spoofed by that toggle — the device
 * still takes touch input — so a touch device inside a laptop's width is
 * treated as hand-held whatever width it claims. A genuine touchscreen
 * laptop only qualifies once its window is under 1100 px, where the
 * stacked layout reads perfectly well anyway.
 */
export const HANDHELD_MAX_WIDTH_PX = 1100;

export function useHandheldLayout(): boolean {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));
  const withinHandheldWidth = useMediaQuery(
    `(max-width:${HANDHELD_MAX_WIDTH_PX}px)`
  );
  const touch =
    typeof navigator !== "undefined" && (navigator.maxTouchPoints ?? 0) > 0;
  return narrow || (touch && withinHandheldWidth);
}
